// Gamification types for the /phishtrainer/me user dashboard.
// See docs/superpowers/plans/2026-04-17-phishtrainer-gamified-dashboard.md.

export type TierKey = 'bronze' | 'silver' | 'gold' | 'diamond' | 'rockstar'

export interface TierDef {
  key: TierKey
  name: string
  threshold: number
}

// Ordered low → high. The minimum threshold (in stars/points) for each tier.
// These MUST match the backend authority (phishtrainer-play-features.service
// `tierThreshold` / phishtrainer-play-contract `tierFromPoints`): the BE drives
// the actual tier + "stars to next tier" text, so a divergent FE scale made the
// progress bar position tiers wrong and contradict the text (#959, #1005) — e.g.
// a 120-star learner read Silver on the BE but Gold on the FE bar.
export const TIER_DEFS: readonly TierDef[] = [
  { key: 'bronze',   name: 'Bronze',    threshold: 0    },
  { key: 'silver',   name: 'Silver',    threshold: 100  },
  { key: 'gold',     name: 'Gold',      threshold: 500  },
  { key: 'diamond',  name: 'Diamond',   threshold: 1500 },
  { key: 'rockstar', name: 'Rock Star', threshold: 5000 },
] as const

export interface StarsBreakdown {
  pass: number            // count of "pass" bonuses awarded historically
  perfect: number
  hardBeaten: number
  streakMilestone: number
  pillarSlam: number
  total: number           // pre-decay lifetime total (sum of above)
}

export interface StreakInfo {
  current: number         // consecutive calendar days with ≥1 passed round up to today
  best: number            // historical max streak length
  freezesAvailable: number
  freezesEarnedLifetime: number
}

export type AchievementState = 'unlocked' | 'progress' | 'locked'

export interface AchievementDef {
  id: string
  name: string
  description: string
  category: 'discovery' | 'mastery' | 'streak' | 'specialist'
  iconKey: string         // maps to a lucide icon name in UI
  colorHex: string
  target?: number         // for progress-trackable achievements
}

export interface AchievementStatus {
  def: AchievementDef
  state: AchievementState
  progress?: number
  unlockedAt?: string     // ISO timestamp
}

export interface WeekStats {
  rounds: number          // rounds completed (pass+fail) in last 7 days
  passed: number
  perfect: number
  starsEarned: number
}

export interface PillarSlamStatus {
  doneCodes: string[]     // e.g. ['PF1','PF2','PF4']
  total: 5
  awardedThisWeek: boolean
}

export interface UserGamificationSummary {
  userId: string
  lifetimeStars: number           // sum of all round payouts, undecayed
  currentStars: number            // after decay, floored at 0
  decayApplied: number            // lifetimeStars - currentStars
  daysSinceActivity: number
  currentTier: TierKey | null
  peakTier: TierKey | null        // highest tier ever reached; null if never enrolled
  nextTier: TierKey | null        // null if at rockstar
  starsToNextTier: number         // 0 if at rockstar
  progressToNextTier: number      // 0..1
  breakdown: StarsBreakdown
  streak: StreakInfo
  weekStats: WeekStats
  pillarSlam: PillarSlamStatus
  achievements: {
    unlocked: AchievementStatus[]
    inProgress: AchievementStatus[]
    locked: AchievementStatus[]
    next: AchievementStatus | null   // the highest-progress in-progress badge
    latest: AchievementStatus | null // most-recently-unlocked
  }
  weeklyQuests: WeeklyQuestsResult
}

export type WeeklyQuestId = 'perfect-trio' | 'hard-tracker' | 'no-miss-week' | 'first-try-master' | 'streak-5'

export interface WeeklyQuest {
  id: WeeklyQuestId
  name: string
  description: string
  progress: number        // current value (e.g. 2)
  target: number          // threshold (e.g. 3)
  completed: boolean
  starsReward: number     // always 2 for now
  iconKey: 'sparkles' | 'target' | 'shield' | 'zap' | 'flame'
  color: string           // hex
}

export interface WeeklyQuestsResult {
  weekIndex: number       // int ISO week number since epoch
  weekStartISO: string    // Monday 00:00 UTC of the active week
  quests: WeeklyQuest[]   // exactly 3
  totalStarsAvailable: number
  totalStarsEarned: number
}
